import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

type Row = Record<string, any>;
export type AllocationDraft = Record<string, Record<string, number>>;

export interface AllocationBatchPayload {
  season_year: number;
  rows: Array<{
    icestupa_id: string;
    allocations: Array<{
      company_id: string;
      ownership_pct: number;
    }>;
  }>;
}

export const buildAllocationMatrix = (
  glaciers: Row[],
  companies: Row[],
  assignments: Row[]
): AllocationDraft => {
  const matrix: AllocationDraft = {};
  glaciers.forEach((glacier) => {
    const glacierId = String(glacier.icestupa_id);
    matrix[glacierId] = {};
    companies.forEach((company) => {
      matrix[glacierId][String(company.company_id)] = 0;
    });
  });
  assignments.forEach((assignment) => {
    const glacierId = String(assignment.icestupa_id);
    const companyId = String(assignment.company_id);
    if (matrix[glacierId] && companyId in matrix[glacierId]) {
      matrix[glacierId][companyId] += Number(assignment.ownership_pct || 0);
    }
  });
  return matrix;
};

export const allocationRowTotal = (
  draft: AllocationDraft,
  glacierId: string
): number =>
  Object.values(draft[glacierId] || {}).reduce(
    (total, value) => total + (Number.isFinite(value) ? value : 0),
    0
  );

export const buildAllocationPayload = (
  seasonYear: number,
  glaciers: Row[],
  companies: Row[],
  draft: AllocationDraft,
  baseline?: AllocationDraft
): AllocationBatchPayload => ({
  season_year: seasonYear,
  rows: glaciers
    .filter((glacier) => {
      if (!baseline) return true;
      const glacierId = String(glacier.icestupa_id);
      return companies.some((company) => {
        const companyId = String(company.company_id);
        return (
          (draft[glacierId]?.[companyId] || 0)
          !== (baseline[glacierId]?.[companyId] || 0)
        );
      });
    })
    .map((glacier) => {
      const glacierId = String(glacier.icestupa_id);
      return {
        icestupa_id: glacierId,
        allocations: companies.map((company) => {
          const companyId = String(company.company_id);
          return {
            company_id: companyId,
            ownership_pct: draft[glacierId]?.[companyId] || 0,
          };
        }),
      };
    }),
});

const AllocationMatrix = ({
  companies,
  glaciers,
  assignments,
  loading,
  loadError,
  saving,
  onSave,
}: {
  companies: Row[];
  glaciers: Row[];
  assignments: Row[];
  loading: boolean;
  loadError?: string;
  saving: boolean;
  onSave: (payload: AllocationBatchPayload) => Promise<boolean>;
}) => {
  const baseline = useMemo(
    () => buildAllocationMatrix(glaciers, companies, assignments),
    [assignments, companies, glaciers]
  );
  const baselineKey = JSON.stringify(baseline);
  const [draft, setDraft] = useState<AllocationDraft>(baseline);

  useEffect(() => {
    setDraft(baseline);
  }, [baseline, baselineKey]);

  const seasonYear = Number(
    String(glaciers[0]?.season_start || assignments[0]?.season_year || new Date().getFullYear())
      .slice(0, 4)
  );
  const draftKey = JSON.stringify(draft);
  const dirty = draftKey !== baselineKey;
  const invalidRows = glaciers.filter((glacier) => {
    const glacierId = String(glacier.icestupa_id);
    const values = Object.values(draft[glacierId] || {});
    const total = allocationRowTotal(draft, glacierId);
    return (
      values.some(
        (value) => !Number.isFinite(value) || value < 0 || value > 100
      )
      || total > 100.000001
    );
  });

  const updateCell = (
    glacierId: string,
    companyId: string,
    value: number
  ) => {
    setDraft((current) => ({
      ...current,
      [glacierId]: {
        ...(current[glacierId] || {}),
        [companyId]: value,
      },
    }));
  };

  if (loadError) return <Alert severity="error">{loadError}</Alert>;
  if (loading) {
    return <Typography color="text.secondary">Loading allocation matrix…</Typography>;
  }
  if (!glaciers.length) {
    return <Alert severity="info">No active glaciers are available.</Alert>;
  }
  if (!companies.length) {
    return <Alert severity="warning">Create an active company before assigning glaciers.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        spacing={1}
      >
        <div>
          <Typography variant="h6">Current-season glacier allocations</Typography>
          <Typography color="text.secondary" variant="body2">
            Enter each company’s percentage. Rows may total from 0% to 100%;
            any remainder stays unassigned.
          </Typography>
        </div>
        <Chip label={`Season ${seasonYear}`} color="primary" variant="outlined" />
      </Stack>

      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Table size="small" aria-label="Glacier allocation matrix">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ fontWeight: 700, backgroundColor: '#f1f5f9', minWidth: 180 }}
              >
                Glacier
              </TableCell>
              {companies.map((company) => (
                <TableCell
                  key={company.company_id}
                  align="center"
                  sx={{ fontWeight: 700, backgroundColor: '#f1f5f9', minWidth: 130 }}
                >
                  {company.display_name || company.company_name || company.company_code}
                </TableCell>
              ))}
              <TableCell
                align="right"
                sx={{ fontWeight: 700, backgroundColor: '#f1f5f9', minWidth: 100 }}
              >
                Allocated
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 700, backgroundColor: '#f1f5f9', minWidth: 110 }}
              >
                Unassigned
              </TableCell>
              <TableCell
                sx={{ fontWeight: 700, backgroundColor: '#f1f5f9', minWidth: 130 }}
              >
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {glaciers.map((glacier) => {
              const glacierId = String(glacier.icestupa_id);
              const total = allocationRowTotal(draft, glacierId);
              const invalid = total > 100.000001 || total < 0;
              const unassigned = Math.max(0, 100 - total);
              const statusLabel = invalid
                ? 'Over allocated'
                : total === 0
                  ? 'Unassigned'
                  : total < 100
                    ? 'Partially assigned'
                    : 'Fully assigned';
              return (
                <TableRow
                  key={glacierId}
                  hover
                  sx={{
                    backgroundColor: invalid ? '#fff7ed' : 'inherit',
                    '&:nth-of-type(even)': {
                      backgroundColor: invalid ? '#fff7ed' : '#f8fafc',
                    },
                  }}
                >
                  <TableCell>
                    <Typography fontWeight={700}>
                      {glacier.display_name || glacierId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {glacierId}
                    </Typography>
                  </TableCell>
                  {companies.map((company) => {
                    const companyId = String(company.company_id);
                    const value = draft[glacierId]?.[companyId] || 0;
                    const changed =
                      value !== (baseline[glacierId]?.[companyId] || 0);
                    return (
                      <TableCell key={companyId} align="center">
                        <TextField
                          value={value}
                          type="number"
                          size="small"
                          error={!Number.isFinite(value) || value < 0 || value > 100}
                          inputProps={{
                            min: 0,
                            max: 100,
                            step: 1,
                            'aria-label': `${glacierId} ${
                              company.display_name ||
                              company.company_name ||
                              company.company_code
                            } ownership percentage`,
                          }}
                          onChange={(event) =>
                            updateCell(
                              glacierId,
                              companyId,
                              event.target.value === ''
                                ? 0
                                : Number(event.target.value)
                            )
                          }
                          sx={{
                            width: 100,
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: changed ? '#eff6ff' : 'white',
                            },
                          }}
                        />
                      </TableCell>
                    );
                  })}
                  <TableCell align="right">
                    <Typography fontWeight={700} color={invalid ? 'error' : 'text.primary'}>
                      {total}%
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{unassigned}%</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={statusLabel}
                      color={
                        invalid
                          ? 'error'
                          : total === 100
                            ? 'success'
                            : total === 0
                              ? 'default'
                              : 'warning'
                      }
                      variant={total === 100 ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {invalidRows.length > 0 && (
        <Alert severity="error">
          Reduce allocations for{' '}
          {invalidRows.map((row) => row.icestupa_id).join(', ')} to 100% or less.
        </Alert>
      )}

      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="contained"
          disabled={!dirty || invalidRows.length > 0 || saving}
          onClick={() =>
            onSave(
              buildAllocationPayload(
                seasonYear,
                glaciers,
                companies,
                draft,
                baseline
              )
            )
          }
        >
          {saving ? 'Saving allocations…' : 'Save all changes'}
        </Button>
        <Button
          variant="outlined"
          disabled={!dirty || saving}
          onClick={() => setDraft(baseline)}
        >
          Reset
        </Button>
        <Typography variant="body2" color="text.secondary">
          {dirty ? 'Unsaved allocation changes' : 'All allocation changes saved'}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default AllocationMatrix;
